import { useState } from 'react';
import type { FC } from 'react';
import { Dialog } from '../../components/ui/dialog';
import { useBalance } from '../../contexts/BalanceContext';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from '../../i18n/I18nContext';
import { apiClient } from '../../utils/api-client';

/**
 * Prepaid-balance top-up dialog.
 *
 * Shows a 2×2 grid of preset amounts (in the user's display currency,
 * smallest-unit-cents) and a free-form input for anything outside the
 * presets. On selection, POSTs to the Stripe checkout-session endpoint
 * and redirects the browser to the returned Checkout URL. The actual
 * balance credit happens when Stripe's webhook fires — Layout polls
 * the balance API after the user returns via `?success=true`.
 *
 * The dialog previously lived inline in Layout.tsx; it was extracted
 * because the checkout flow (state, handlers, API call, preset logic)
 * has nothing structurally to do with the app shell. Layout now
 * renders <AddFundsDialog open={...} onClose={...} /> in its dialog
 * pile and owns nothing beyond the open/close state.
 */

const CUSTOM_MIN = 2;
const CUSTOM_MAX = 500;

// Preset amounts in the smallest unit of the user's display currency,
// mapped per currency. Every currency currently uses the same grid; the
// record lets us tune per-currency presets later without touching the
// render.
const PRESETS_BY_CURRENCY: Record<string, number[]> = {
  GBP: [200, 500, 1000, 2000],
  EUR: [200, 500, 1000, 2000],
  USD: [200, 500, 1000, 2000],
};
const DEFAULT_PRESETS = [200, 500, 1000, 2000];

interface AddFundsDialogProps {
  open: boolean;
  onClose: () => void;
}

const AddFundsDialog: FC<AddFundsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { balance } = useBalance();
  const { showToast } = useToast();
  const [customAmount, setCustomAmount] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const currencySymbol = balance?.currencySymbol || '$';
  const presets = balance ? (PRESETS_BY_CURRENCY[balance.displayCurrency] ?? DEFAULT_PRESETS) : DEFAULT_PRESETS;

  const handleClose = () => {
    setCustomAmount('');
    onClose();
  };

  const handleAddFunds = async (localCents: number) => {
    setCheckoutLoading(true);
    try {
      const response = await apiClient.post<{ sessionId: string; url: string }>(
        '/accounts/stripe/create-checkout-session',
        {
          localAmountCents: localCents,
          displayCurrency: balance?.displayCurrency || 'USD',
        }
      );
      window.location.href = response.url;
    } catch {
      showToast(t('funds.checkout_failed'), 'error');
      setCheckoutLoading(false);
    }
  };

  const handleCustomAmount = () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < CUSTOM_MIN || amount > CUSTOM_MAX) {
      showToast(
        t('funds.amount_error', { symbol: currencySymbol, min: String(CUSTOM_MIN), max: String(CUSTOM_MAX) }),
        'warning',
      );
      return;
    }
    handleAddFunds(Math.round(amount * 100));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('funds.title')}
      maxWidth="sm"
      footer={
        <div className="flex items-center justify-end">
          <button
            onClick={handleClose}
            disabled={checkoutLoading}
            className="px-4 py-2 rounded-md text-sm border border-border text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
        </div>
      }
    >
      <div className="px-4 sm:px-6 py-4">
        {balance && (
          <>
            {checkoutLoading && (
              <div
                role="status"
                aria-live="polite"
                className="mb-3 flex items-center gap-2 rounded border border-info/40 bg-info/10 px-3 py-2 text-sm text-foreground"
              >
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-info border-t-transparent" />
                {t('funds.connecting')}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {presets.map((cents) => (
                <button
                  key={cents}
                  className="border border-foreground/40 text-foreground px-4 py-2 rounded font-semibold hover:bg-primary hover:text-primary-foreground hover:border-primary disabled:opacity-50 transition-colors"
                  onClick={() => handleAddFunds(cents)}
                  disabled={checkoutLoading}
                >
                  {currencySymbol}{cents / 100}
                </button>
              ))}
            </div>
            <div className="flex">
              <span className="inline-flex items-center px-3 border border-r-0 border-input rounded-l bg-muted text-muted-foreground">{currencySymbol}</span>
              <input
                type="number"
                className="flex-1 border border-input px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder={t('funds.custom_placeholder', { min: String(CUSTOM_MIN), max: String(CUSTOM_MAX) })}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                min={CUSTOM_MIN}
                max={CUSTOM_MAX}
                step="1"
                disabled={checkoutLoading}
              />
              <button
                className="bg-primary text-primary-foreground px-4 py-2 rounded-r font-semibold hover:opacity-90 disabled:opacity-50"
                onClick={handleCustomAmount}
                disabled={checkoutLoading || !customAmount}
              >
                {checkoutLoading ? '...' : t('funds.add')}
              </button>
            </div>
            <small className="text-muted-foreground block mt-2">
              {t('funds.help', { symbol: currencySymbol, min: String(CUSTOM_MIN), max: String(CUSTOM_MAX) })}
            </small>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default AddFundsDialog;
